// frontend/src/components/admin/AssociationDashboard.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Card from '../common/Card';
import Button from '../common/Button';
import Loading from '../common/Loading';
import ConsumptionChart from './ConsumptionChart';
import ThresholdConfig from './ThresholdConfig';

const AssociationDashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [controlling, setControlling] = useState(false);
  const [showThresholdConfig, setShowThresholdConfig] = useState(false);
  
  const [historyData, setHistoryData] = useState([]);
  const [historyPeriod, setHistoryPeriod] = useState('week');
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchHistoryData(historyPeriod);
    
    const interval = setInterval(() => {
      fetchDashboardData();
      fetchHistoryData(historyPeriod);
    }, 30000);
    
    return () => clearInterval(interval);
  }, [id, historyPeriod]);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get(`/monitoring/dashboard/${id}`);
      setData(response.data.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistoryData = async (period = 'week') => {
    try {
      setHistoryLoading(true);
      const response = await api.get(`/monitoring/history/${id}?period=${period}`);
      if (response.data.success) {
        setHistoryData(response.data.data);
      }
    } catch (error) {
      console.error('Erreur récupération historique:', error);
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleControl = async (action) => {
    if (controlling) return;
    setControlling(true);
    try {
      await api.post(`/associations/${id}/control`, { action });
      alert(`Switch ${action === 'on' ? 'allumé' : 'éteint'} avec succès`);
      fetchDashboardData();
    } catch (error) {
      alert(error.response?.data?.message || 'Erreur lors du contrôle');
    } finally {
      setControlling(false);
    }
  };

  const handleToggleAutoMode = async () => {
    try {
      await api.patch(`/associations/${id}/auto-mode`, {
        enabled: !data.association.autoMode,
      });
      fetchDashboardData();
    } catch (error) {
      alert(error.response?.data?.message || 'Erreur');
    }
  };

  const handleResetConsumption = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir réinitialiser la consommation ?')) return;
    try {
      await api.post(`/monitoring/reset/${id}`);
      alert('Consommation réinitialisée avec succès');
      fetchDashboardData();
    } catch (error) {
      alert(error.response?.data?.message || 'Erreur');
    }
  };

  const formatCurrency = (amount) => {
    return Math.round(amount).toLocaleString('fr-FR');
  };

  if (loading) return <Loading />;

  if (!data) {
    return (
      <div className="p-6">
        <Card>
          <p className="text-center text-gray-500 py-8">Aucune donnée disponible</p>
        </Card>
      </div>
    );
  }

  const { association, sensor, switch: switchDevice, consumption, costs, threshold } = data;

  // ✅ Ce dashboard est dédié au suivi de consommation électrique (compteur Tuya).
  //    Un Contrôleur IR n'a aucune mesure d'énergie — rediriger vers une page
  //    d'accès direct plutôt que d'afficher des données de facturation à 0/non pertinentes.
  if (association?.switchId?.type === 'ir_controller') {
    return (
      <div className="p-6">
        <button
          onClick={() => navigate(-1)}
          className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
        >
          ← Retour
        </button>
        <Card>
          <div className="text-center py-12">
            <p className="text-4xl mb-3">🌡️</p>
            <p className="text-gray-700 font-semibold mb-1">
              "{association.name}" pilote un Contrôleur IR (climatisation)
            </p>
            <p className="text-gray-500 text-sm">
              Ce type d'équipement n'a pas de mesure de consommation électrique.
              Utilisez le bouton "🌡️ Piloter la climatisation" depuis la liste des
              associations pour le contrôler.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <button
            onClick={() => navigate('/associations')}
            className="text-blue-600 hover:text-blue-800 mb-2 flex items-center"
          >
            ← Retour aux associations
          </button>
          <h1 className="text-3xl font-bold text-gray-800">{association.name}</h1>
          <p className="text-gray-600 mt-1">{association.description}</p>
        </div>
        <div className="flex space-x-3">
          <Button variant="secondary" onClick={() => setShowThresholdConfig(true)}>
            ⚙️ Configurer seuil
          </Button>
          <Button variant="secondary" onClick={handleResetConsumption}>
            🔄 Réinitialiser
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonne 1 : État des devices */}
        <div className="space-y-6">
          {/* Sensor */}
          <Card>
            <h2 className="text-xl font-bold mb-4 flex items-center">
              <span className="text-2xl mr-2">📡</span>Capteur
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Nom:</span>
                <span className="font-bold">{sensor.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Statut:</span>
                {sensor.online ? (
                  <span className="px-3 py-1 bg-green-500 text-white text-sm rounded-full">En ligne</span>
                ) : (
                  <span className="px-3 py-1 bg-red-500 text-white text-sm rounded-full">Hors ligne</span>
                )}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Présence:</span>
                {sensor.hasPresence ? (
                  <span className="px-3 py-1 bg-yellow-500 text-white text-sm rounded-full flex items-center">
                    <span className="animate-pulse mr-2">👤</span>Détectée
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-gray-300 text-gray-700 text-sm rounded-full">Aucune</span>
                )}
              </div>
              <div className="pt-3 border-t">
                <p className="text-xs text-gray-500 mb-2">ID Device</p>
                <code className="text-xs bg-gray-100 px-2 py-1 rounded block break-all">
                  {sensor.deviceId}
                </code>
              </div>
            </div>
          </Card>

          {/* Switch */}
          <Card>
            <h2 className="text-xl font-bold mb-4 flex items-center">
              <span className="text-2xl mr-2">💡</span>Interrupteur
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Nom:</span>
                <span className="font-bold">{switchDevice.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Statut:</span>
                {switchDevice.online ? (
                  <span className="px-3 py-1 bg-green-500 text-white text-sm rounded-full">En ligne</span>
                ) : (
                  <span className="px-3 py-1 bg-red-500 text-white text-sm rounded-full">Hors ligne</span>
                )}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">État:</span>
                {switchDevice.isOn ? (
                  <span className="px-3 py-1 bg-green-600 text-white text-sm rounded-full font-bold">
                    ⚡ ALLUMÉ
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-gray-400 text-white text-sm rounded-full">
                    ⭕ ÉTEINT
                  </span>
                )}
              </div>
              <div className="pt-3 border-t">
                <p className="text-xs text-gray-500 mb-2">ID Device</p>
                <code className="text-xs bg-gray-100 px-2 py-1 rounded block break-all">
                  {switchDevice.deviceId}
                </code>
              </div>
            </div>
          </Card>

          {/* Contrôle Manuel */}
          <Card>
            <h2 className="text-xl font-bold mb-4">Contrôle Manuel</h2>
            <div className="space-y-3">
              <button
                onClick={() => handleControl('on')}
                disabled={controlling || !switchDevice.online}
                className={`w-full px-4 py-3 rounded-lg font-bold text-lg transition ${
                  switchDevice.isOn ? 'bg-green-600 text-white' : 'bg-green-500 text-white hover:bg-green-600'
                } disabled:bg-gray-300 disabled:cursor-not-allowed`}
              >
                {controlling ? '⏳' : '🔆'} ALLUMER
              </button>
              <button
                onClick={() => handleControl('off')}
                disabled={controlling || !switchDevice.online}
                className={`w-full px-4 py-3 rounded-lg font-bold text-lg transition ${
                  !switchDevice.isOn ? 'bg-red-600 text-white' : 'bg-red-500 text-white hover:bg-red-600'
                } disabled:bg-gray-300 disabled:cursor-not-allowed`}
              >
                {controlling ? '⏳' : '🌙'} ÉTEINDRE
              </button>
              <button
                onClick={handleToggleAutoMode}
                className={`w-full px-4 py-3 rounded-lg font-semibold transition ${
                  association.autoMode
                    ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {association.autoMode ? '🤖 Mode Auto: ON' : '🔧 Mode Auto: OFF'}
              </button>
            </div>
          </Card>
        </div>

        {/* Colonne 2 : Consommation */}
        <div className="lg:col-span-2 space-y-6">

          {/* Consommation en temps réel */}
          <Card>
            <h2 className="text-xl font-bold mb-4">⚡ Consommation en temps réel</h2>
            {consumption ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Puissance → valeur temps réel, OK */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Puissance</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {consumption.power.toFixed(1)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Watts</p>
                </div>

                {/* ✅ CORRIGÉ : Énergie du mois = costs.consumption.kwh (différence) */}
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Énergie ce mois</p>
                  <p className="text-2xl font-bold text-green-600">
                    {costs ? costs.consumption.kwh.toFixed(3) : '0.000'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">kWh (depuis le 1er)</p>
                </div>

                {/* Courant → temps réel, OK */}
                <div className="bg-yellow-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Courant</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {consumption.current.toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Ampères</p>
                </div>

                {/* Tension → temps réel, OK */}
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Tension</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {consumption.voltage.toFixed(1)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Volts</p>
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">
                Données de consommation non disponibles
              </p>
            )}
          </Card>

          {/* Coûts */}
          {costs && (
            <Card>
              <h2 className="text-xl font-bold mb-4">💰 Détail des coûts ce mois</h2>
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg p-6">
                  <p className="text-sm opacity-90 mb-2">Coût de base (sans taxes)</p>
                  <p className="text-4xl font-bold">
                    {formatCurrency(costs.totals.costWithoutTaxes)}
                  </p>
                  <p className="text-sm opacity-90 mt-1">FCFA</p>
                </div>

                <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg p-6">
                  <p className="text-sm opacity-90 mb-2">Facture totale (avec taxes et redevances)</p>
                  <p className="text-4xl font-bold">
                    {formatCurrency(costs.totals.totalCost)}
                  </p>
                  <p className="text-sm opacity-90 mt-1">FCFA</p>
                </div>

                <div className="space-y-2 text-sm">
                  {/* ✅ CORRIGÉ : affiche costs.consumption.kwh (différence du mois) */}
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Consommation ({costs.consumption.kwh.toFixed(3)} kWh × {costs.consumption.pricePerKwh || 81.03} FCFA)
                    </span>
                    <span className="font-semibold">
                      {formatCurrency(costs.consumption.baseCost)} FCFA
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">TVA consommation (18%)</span>
                    <span className="font-semibold">
                      {formatCurrency(costs.consumption.tvaConsommation)} FCFA
                    </span>
                  </div>

                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Prime fixe</span>
                      <span className="font-semibold">
                        {formatCurrency(costs.fixedCharges.primeFixe)} FCFA
                      </span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-gray-600">TVA prime fixe (18%)</span>
                      <span className="font-semibold">
                        {formatCurrency(costs.fixedCharges.tvaPrimeFixe)} FCFA
                      </span>
                    </div>
                  </div>

                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Électrification rurale</span>
                      <span className="text-gray-800">
                        {formatCurrency(costs.taxes.redevanceElectrificationRurale)} FCFA
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Ordures ménagères</span>
                      <span className="text-gray-800">
                        {formatCurrency(costs.taxes.taxeOrduresMenageres)} FCFA
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">RTI</span>
                      <span className="text-gray-800">
                        {formatCurrency(costs.taxes.redevanceRTI)} FCFA
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Timbre d'État</span>
                      <span className="text-gray-800">
                        {formatCurrency(costs.taxes.timbreEtat)} FCFA
                      </span>
                    </div>
                    <div className="flex justify-between mt-1 font-semibold">
                      <span className="text-gray-600">Total redevances</span>
                      <span className="text-gray-800">
                        {formatCurrency(costs.taxes.totalRedevances)} FCFA
                      </span>
                    </div>
                  </div>

                  <div className="border-t pt-2 mt-2 font-bold text-base">
                    <div className="flex justify-between">
                      <span>TVA totale</span>
                      <span className="text-blue-600">
                        {formatCurrency(costs.totals.totalTVA)} FCFA
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Seuil de consommation */}
          <Card>
            <h2 className="text-xl font-bold mb-4">📊 Seuil de consommation</h2>
            <div className="space-y-4">
              {threshold?.enabled ? (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Seuil configuré:</span>
                    <span className="font-bold text-lg">
                      {formatCurrency(threshold.value)} {threshold.type === 'kwh' ? 'kWh' : 'FCFA'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Consommation actuelle:</span>
                    <span className="font-bold text-lg">
                      {formatCurrency(threshold.current)} {threshold.type === 'kwh' ? 'kWh' : 'FCFA'}
                    </span>
                  </div>
                  <div className="mt-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span>Progression</span>
                      <span className="font-bold">{threshold.percentage.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                      <div
                        className={`h-4 rounded-full transition-all duration-500 ${
                          threshold.exceeded
                            ? 'bg-red-500'
                            : threshold.percentage >= 80
                            ? 'bg-yellow-500'
                            : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(threshold.percentage, 100)}%` }}
                      />
                    </div>
                    {threshold.exceeded && (
                      <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-red-700 font-semibold flex items-center">
                          <span className="text-xl mr-2">⚠️</span>Seuil dépassé !
                        </p>
                      </div>
                    )}
                    {!threshold.exceeded && threshold.percentage >= 80 && (
                      <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <p className="text-yellow-700 font-semibold flex items-center">
                          <span className="text-xl mr-2">⚠️</span>
                          Attention : {threshold.percentage.toFixed(1)}% du seuil atteint
                        </p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">Aucun seuil de consommation configuré</p>
                  <Button onClick={() => setShowThresholdConfig(true)}>
                    ⚙️ Configurer un seuil
                  </Button>
                </div>
              )}
            </div>
          </Card>

          {/* Historique */}
          <Card>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">📈 Historique de consommation</h2>
              <div className="flex space-x-2">
                {['week', 'month', 'year'].map((p) => (
                  <button
                    key={p}
                    onClick={() => setHistoryPeriod(p)}
                    className={`px-3 py-1 rounded text-sm transition ${
                      historyPeriod === p
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {p === 'week' ? 'Semaine' : p === 'month' ? 'Mois' : 'Année'}
                  </button>
                ))}
              </div>
            </div>
            {historyLoading ? (
              <div className="text-center text-gray-500 py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-2">Chargement de l'historique...</p>
              </div>
            ) : historyData.length > 0 ? (
              <>
                <ConsumptionChart data={historyData} type="line" />
                <p className="text-xs text-gray-500 text-center mt-4">
                  ✅ {historyData.length} entrées
                </p>
              </>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <p className="mb-2">📊 Aucun historique disponible</p>
                <p className="text-sm">Les données seront collectées automatiquement</p>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Modal Seuil */}
      {showThresholdConfig && (
        <ThresholdConfig
          associationId={id}
          currentThreshold={threshold}
          onClose={() => setShowThresholdConfig(false)}
          onSuccess={() => {
            setShowThresholdConfig(false);
            fetchDashboardData();
          }}
        />
      )}
    </div>
  );
};

export default AssociationDashboard;